import { useWatch } from 'react-hook-form';
import { useCallback } from 'react';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { PatientMedicine } from '@dreryk/egabinet-prescription-frontend-api';

import { ActionInput, Heading, StyledFlex } from '@dreryk/react-components';
import { FormContextValues } from '@dreryk/react-common';

import { VisitDiagnoses } from '~libs.shared.visit';

import { NewMedicineFieldFormBeanPart } from '../NewMedicineField.types';
import { medicineTranslationKeys } from '../../../../../../../translations';
import { PharmindexLogo } from '../../../../../MedicineSelector/PharmindexLogo/PharmindexLogo';
import { MedicineSimilarTable } from '../similar/MedicineSimilarTable';

import { StyledHeader } from './MedicineSelector.styled';
import { MedicineSearchTable } from './MedicineSearchTable';

const SEARCH_TEXT_MIN_LENGTH = 3;

export type MedicineSelectorProps = Pick<
   FormContextValues<NewMedicineFieldFormBeanPart>,
   'register' | 'control' | 'setValue'
> & {
   patientId: Patient['id'];
   withdrawnMedicineId: number;
   visitDiagnoses: VisitDiagnoses;
};

export const MedicineSelector = (props: MedicineSelectorProps): JSX.Element => {
   const searchText = useWatch<string | null>({
      control: props.control,
      name: 'searchText',
      defaultValue: null,
   });

   const handleMedicineSelection = useCallback(
      (medicine: PatientMedicine): void => {
         props.setValue('newMedicine', medicine, { shouldValidate: true, shouldDirty: true });
      },
      [props]
   );

   return (
      <StyledFlex direction={'column'} gap={'8px'} width={'100%'}>
         <StyledHeader>
            <Heading
               testId={'newMedicine'}
               type={'h2'}
               content={medicineTranslationKeys.replacement.chooseNewMedicine}
               margin={'0px'}
            />
            <PharmindexLogo />
         </StyledHeader>

         <ActionInput
            controller={props.register('searchText')}
            testId={'searchText'}
            placeholder={
               medicineTranslationKeys.replacement.searchForMedicineByTradeNameOrActiveIngredientOrICD10codePlaceholder
            }
            autoFocus
         />
         {searchText && searchText.length >= SEARCH_TEXT_MIN_LENGTH ? (
            <MedicineSearchTable
               patientId={props.patientId}
               searchText={searchText}
               onMedicineSelection={handleMedicineSelection}
               visitDiagnoses={props.visitDiagnoses}
            />
         ) : (
            <MedicineSimilarTable
               patientId={props.patientId}
               onMedicineSelection={handleMedicineSelection}
               visitDiagnoses={props.visitDiagnoses}
               withdrawnMedicineId={props.withdrawnMedicineId}
            />
         )}
      </StyledFlex>
   );
};
